import React, {useEffect, useMemo, useState} from "react";
import {Alert, Box, Button, CircularProgress, Stack, Typography, useTheme} from "@mui/material";
import {Instant, Now, useFlag, ZonedDateTime} from "@variocube/app-ui";
import {useAsync} from "react-async-hook";
import {isNull} from "lodash";
import {notBlank} from "../../../utils";
import {useLocalization} from "../../../i18n";
import {useBooking} from "../../../context/booking";
import {BoxedInfo} from "../../../component/boxed-info";
import {B2Occupancy} from "../../../data/client.gen";
import {BookingBoxes, BookingEventType, LockStatus, useBookingClient, useBookingEvent} from "../../../data/bookings";
import {BookingOpenButton} from "./booking-open";
import {BookingInfoButton} from "./booking-info";

let boxOpeningTimeout: NodeJS.Timeout;

export function BookingView() {
	const {t} = useLocalization();
	const {booking, bookingFrom, bookingUntil, occupy, open, release} = useBooking();

	const [boxOpened, setBoxOpened] = useFlag(false);
	const [insufficientLockers, setInsufficientLockers] = useState(false);
	const [boxOpeningError, setBoxOpeningError, clearBoxOpeningError] = useFlag(false);
	const [occupancies, setOccupancies] = useState<B2Occupancy[]>(booking?.occupancies ?? []);

	/**
	 * Booking is started when the booking from is before current time.
	 */
	const isStarted = useMemo(() =>
			bookingFrom !== undefined && ZonedDateTime.compare(bookingFrom, Now.zonedDateTimeISO()) < 1
		, [bookingFrom]);

	/**
	 * Booking is ended when the booking until is before current time.
	 */
	const isEnded = useMemo(() =>
			bookingUntil !== undefined && ZonedDateTime.compare(bookingUntil, Now.zonedDateTimeISO()) < 1
		, [bookingUntil]);

	/**
	 * Denotes whether the booking is eligible for ending process. Requirements:
	 * - site must have `enabledBookingEndAfter2ndOpening` flag enabled
	 * - has at least 1 occupancy which was opened at least once
	 * - exceed the grace period of site configuration (if configured)
	 *
	 * Use this flag to warn the user of their booking will be automatically ended upon opening.
	 */
	const isEligibleForBookingEnd = useMemo(() => {
		if (booking !== undefined && booking.site.enabledBookingEndAfter2ndOpening) {
			console.group("Booking end after 2nd opening enabled on site.");
			const occupancy = occupancies.find(o => (o.openedTimes ?? 0) > 0);
			if (occupancy !== null) {
				console.info('Booking has occupancy that was opened at least once.');
				if (isNull(booking.site.gracePeriodOf2ndOpening)) {
					console.info("Grace period not configured on site. Booking end is effective immediately.");
				} else if (notBlank(occupancy?.occupiedFrom)) {
					const occupiedFrom = Instant.from(occupancy?.occupiedFrom as string);
					const effectiveUntil = Now.zonedDateTimeISO().subtract({ minutes: booking.site.gracePeriodOf2ndOpening }).toInstant();
					if (Instant.compare(occupiedFrom, effectiveUntil) < 1) {
						console.info("Booking has past the grace period and is now eligible for ending after 2nd opening.")
						return true;
					}
				}
			}
		}
		return false;
	}, [booking, occupancies]);

	/**
	 * Automatically occupied boxes when the booking:
	 * - has no occupancies
	 * - site is configured with a default cube id
	 * - booking has started
	 * - booking not yet ended
	 */
	const {result: occupied, error: occupyError, loading: occupyLoading} = useAsync(async () => {
		if (booking !== undefined
			&& occupancies.length === 0
			&& notBlank(booking.site.automaticallySelectCubeId)
			&& isStarted
			&& !isEnded
		) {
			return await occupy(booking.site.automaticallySelectCubeId as string);
		}
		return undefined;
	}, [booking, occupancies, isStarted]);

	useEffect(() => {
		if (booking !== undefined && occupied !== undefined) {
			const totalBoxesCount = Object.values(booking.boxes as BookingBoxes).reduce((a, b) => a + b, 0);
			setInsufficientLockers(occupied.length !== totalBoxesCount);
			setOccupancies(occupied);
		}
	}, [booking, occupied]);

	if (booking !== undefined) {
		const client = useBookingClient(booking.id);
		useBookingEvent(client, BookingEventType.B2OccupancyEvent, event => {
			console.info('Lock status changed on box ', event.boxNumber)
			if (event.lockStatus === LockStatus.Open) {
				setBoxOpened();
				clearBoxOpeningError();
				if (boxOpeningTimeout !== undefined) {
					clearTimeout(boxOpeningTimeout);
					console.info('Cleared box opening timeout.');
				}
			}
			setOccupancies(prev => prev.map(o => {
				if (o.uuid === event.uuid) {
					o.lockStatus = event.lockStatus;
					o.openedTimes = event.openedTimes;
				}
				return o;
			}));
		});
	}

	async function handleOpenBox() {
		if (booking !== undefined) {
			try {
				await Promise.all(occupancies.map(open));
				if (isEligibleForBookingEnd) {
					await release();
				}
				boxOpeningTimeout = setTimeout(() => {
					setBoxOpeningError();
				}, 5000);
			} catch (err) {
				console.error('Failed to open box(es)', err);
				setBoxOpeningError();
			}
		}
	}

	const theme = useTheme();
	return (
		<>
			{!boxOpened && (
				<>
					<Typography textAlign="center" variant="h1" gutterBottom>{t('booking.simplified.title')}</Typography>
					{!isStarted && <Typography variant="subtitle1" textAlign="center">{t('booking.simplified.notYetStarted')}</Typography>}
					{isStarted && (
						<>
							{isEnded && <Typography variant="subtitle1" textAlign="center">{t('booking.simplified.ended')}</Typography>}
							{!isEnded && (
								<Stack
									direction="column"
									gap={1}
								>
									<div>
										<BoxedInfo
											label={t('booking.simplified.box')}
											content={
												<>
													{occupyLoading && <CircularProgress size={20} />}
													<Typography fontWeight={600} fontSize="2.5rem" lineHeight="1em">{occupancies.map(o => o.boxNumber).join(", ")}</Typography>
												</>
											}
										/>
									</div>
									<Typography variant="subtitle1">
										<strong style={{ color: theme.palette.error.main, textTransform: 'uppercase' }}>{t('booking.simplified.warning.leading')}</strong>
										&nbsp;
										{t('booking.simplified.warning.trailing')}
									</Typography>
									<Typography variant="subtitle1">
										<strong style={{ color: theme.palette.error.main, textTransform: 'uppercase' }}>{t('booking.simplified.attention.leading')}</strong>
										&nbsp;
										{t('booking.simplified.attention.trailing')}
									</Typography>
									{occupyError && <Alert severity="error">{t('booking.occupy.error')}</Alert>}
									{insufficientLockers && (
										<Alert
											severity="warning"
											action={
												<Button
													variant="contained"
													color="primary"
													onClick={() => window.location.reload()}
												>
													{t('booking.simplified.retry')}
												</Button>
											}
										>
											{t('booking.simplified.insufficientLockers')}
										</Alert>
									)}
									{boxOpeningError && <Alert severity="error">{t('boxes.open.error')}</Alert>}
								</Stack>
							)}
						</>
					)}
					<Box my={1} flexGrow={1} />
					{isStarted && !isEnded && (
						<>
							<BookingOpenButton
								disabled={occupancies.length === 0}
								releaseWarning={isEligibleForBookingEnd}
								onOpen={handleOpenBox}
							/>
							<Box my={1} />
						</>
					)}
					<BookingInfoButton />
				</>
			)}
			{boxOpened && (
				<>
					<Box my={1} flexGrow={1} />
					<Typography align="center" variant="h2">
						{isEligibleForBookingEnd && t('booking.simplified.boxOpening.farewellMessage')}
						{!isEligibleForBookingEnd && t('booking.simplified.boxOpening.successMessage')}
					</Typography>
					<Box my={1} flexGrow={1} />
					{!isEligibleForBookingEnd && (
						<Button
							variant="contained"
							color="primary"
							disableElevation
							onClick={() => location.reload()}
						>
							{t('booking.simplified.cta')}
						</Button>
					)}
				</>
			)}
		</>
	)
}
